import { INTEGRATION_SETTING_TYPE } from '../../../../common/constants/IntegrationConstants';
import {
  CONFIG_STATUS_LEVEL,
  ENTITY_LEVEL,
} from '../../../../common/constants/integrationConstants/crmConstants';
import { getNoteSyncConfigName } from '../../../../common/util/crmIntegrationUtils/crmUtils';
import {
  cancelNoteSyncStatus,
  changeNoteEntityTab,
  changeNoteSyncStatus,
  clearUnsavedChanges,
  fetchNoteSyncStatus,
  getNoteSyncEditStatus,
  saveNoteSyncStatus,
} from '../../../routines/integrations/crmRoutines';

const initialState = {
  integrationConfigs: {
    organizationNaVendorToVelarisNaNoteSync: false,
    accountNaVendorToVelarisNaNoteSync: false,
    organizationNaVelarisToVendorNaNoteSync: false,
    accountNaVelarisToVendorNaNoteSync: false,
    organizationCreateVendorToVelarisEventDrivenNoteSync: false,
    organizationUpdateVendorToVelarisEventDrivenNoteSync: false,
    accountCreateVendorToVelarisEventDrivenNoteSync: false,
    accountUpdateVendorToVelarisEventDrivenNoteSync: false,
    organizationCreateVelarisToVendorNaNoteSync: false,
    organizationUpdateVelarisToVendorNaNoteSync: false,
    accountCreateVelarisToVendorNaNoteSync: false,
    accountUpdateVelarisToVendorNaNoteSync: false,
    organizationAllVendorToVelarisDataDrivenNoteSync: false,
    accountAllVendorToVelarisDataDrivenNoteSync: false,
  },
  entityLevel: ENTITY_LEVEL.ORGANIZATION,
  persistedStateNoteSync: {},
  loading: false,
  error: null,
  hasUnSavedConfig: false,
  hasUnSavedOrganizationConfig: false,
  hasUnSavedAccountConfig: false,
  disabledOrg: true,
  disabledAccount: true,
};

const crmNoteSyncReducer = (state = initialState, action) => {
  switch (action.type) {
    case saveNoteSyncStatus.TRIGGER:
      return {
        ...state,
      };
    case saveNoteSyncStatus.SUCCESS: {
      const { integrationConfigs, entityLevel } = action.payload;

      const newNoteSyncState = {};
      integrationConfigs.forEach((config) => {
        const keyName = getNoteSyncConfigName(
          config.entityLevel,
          config.criteria,
          config.syncDirection,
          config.syncType
        );
        newNoteSyncState[keyName] = config.enable;
      });

      const isOrganizationSaved = entityLevel === ENTITY_LEVEL.ORGANIZATION;
      const isAccountSaved = entityLevel === ENTITY_LEVEL.ACCOUNT;

      return {
        ...state,
        integrationConfigs: {
          ...state.integrationConfigs,
          ...newNoteSyncState,
        },
        persistedStateNoteSync: {
          ...state.persistedStateNoteSync,
          ...newNoteSyncState,
        },
        loading: false,
        error: null,
        hasUnSavedConfig: false,
        hasUnSavedOrganizationConfig: isOrganizationSaved
          ? false
          : state.hasUnSavedOrganizationConfig,
        hasUnSavedAccountConfig: isAccountSaved
          ? false
          : state.hasUnSavedAccountConfig,
      };
    }
    case changeNoteSyncStatus.TRIGGER: {
      const { entityLevel, criteria, syncDirection, noteSync, syncType } =
        action.payload;
      const newNoteSyncState = {
        ...state.integrationConfigs,
        [getNoteSyncConfigName(entityLevel, criteria, syncDirection, syncType)]:
          noteSync,
      };
      const isOrganizationChanged = entityLevel === ENTITY_LEVEL.ORGANIZATION;
      const isAccountChanged = entityLevel === ENTITY_LEVEL.ACCOUNT;

      return {
        ...state,
        integrationConfigs: newNoteSyncState,
        hasUnSavedConfig: true,
        hasUnSavedOrganizationConfig: isOrganizationChanged
          ? true
          : state.hasUnSavedOrganizationConfig,
        hasUnSavedAccountConfig: isAccountChanged
          ? true
          : state.hasUnSavedAccountConfig,
      };
    }
    case clearUnsavedChanges.TRIGGER: {
      const previousNoteSyncStatus = { ...state.persistedStateNoteSync };

      return {
        ...state,
        integrationConfigs: previousNoteSyncStatus,
        hasUnSavedConfig: false,
        hasUnSavedOrganizationConfig: false,
        hasUnSavedAccountConfig: false,
      };
    }
    case cancelNoteSyncStatus.TRIGGER: {
      const { entityLevel } = action.payload;
      const previousNoteSyncStatus = { ...state.persistedStateNoteSync };

      const updateSyncStatus = (level) => {
        return Object.keys(previousNoteSyncStatus).reduce((acc, key) => {
          if (key.includes(level)) {
            acc[key] = previousNoteSyncStatus[key];
          }
          return acc;
        }, {});
      };

      const updatedNoteSyncStatus = updateSyncStatus(entityLevel);

      const isOrganizationChanged = entityLevel === ENTITY_LEVEL.ORGANIZATION;
      const isAccountChanged = entityLevel === ENTITY_LEVEL.ACCOUNT;

      return {
        ...state,
        integrationConfigs: {
          ...state.integrationConfigs,
          ...updatedNoteSyncStatus,
        },
        hasUnSavedConfig: false,
        hasUnSavedOrganizationConfig: isOrganizationChanged
          ? false
          : state.hasUnSavedOrganizationConfig,
        hasUnSavedAccountConfig: isAccountChanged
          ? false
          : state.hasUnSavedAccountConfig,
      };
    }

    case fetchNoteSyncStatus.TRIGGER:
      return {
        ...state,
        loading: true,
        error: null,
      };
    case fetchNoteSyncStatus.SUCCESS: {
      const data = action.payload || {};
      const noteSyncSettingItems = Object.values(data).filter(
        ({ settingType }) => settingType === INTEGRATION_SETTING_TYPE.NOTE_SYNC
      );
      const newNoteSyncState = {
        ...state.integrationConfigs,
      };
      // create key based on entitylevel, criteria, sync direction and syncType
      noteSyncSettingItems.forEach((item) => {
        const keyName = getNoteSyncConfigName(
          item.entityLevel,
          item.criteria,
          item.syncDirection,
          item.syncType
        );
        newNoteSyncState[keyName] = item.enable;
      });

      return {
        ...state,
        integrationConfigs: newNoteSyncState,
        persistedStateNoteSync: newNoteSyncState,
        loading: false,
        error: null,
        hasUnSavedConfig: false,
        hasUnSavedAccountConfig: false,
        hasUnSavedOrganizationConfig: false,
      };
    }
    case changeNoteEntityTab.TRIGGER: {
      const { entityLevel } = action.payload;
      return {
        ...state,
        entityLevel,
      };
    }
    case getNoteSyncEditStatus.TRIGGER: {
      const { deduplication } = action.payload;

      const isDeduplicationConfiguredAccount =
        deduplication[ENTITY_LEVEL.ACCOUNT]?.status ===
        CONFIG_STATUS_LEVEL.SAVED;
      const isDeduplicationConfiguredOrg =
        deduplication[ENTITY_LEVEL.ORGANIZATION]?.status ===
        CONFIG_STATUS_LEVEL.SAVED;
      return {
        ...state,
        disabledOrg: !isDeduplicationConfiguredOrg,
        disabledAccount: !isDeduplicationConfiguredAccount,
      };
    }
    default:
      return state;
  }
};
export default crmNoteSyncReducer;
